// ImageCard.jsx
import React, { useState, useRef, useEffect } from "react";
import { Card, CardBody, CardHeader } from "../card/card.jsx";
import { v4 as uuidv4 } from "uuid";
import useKeyboardShortcut from "../../hooks/useKeyboardShortcut.js";
import CONFIG from "../../config/config.js";
import { getAuthHeaders } from "../../misc.js";

const ImageCard = ({
  label,
  imageData,
  onPromptChange,
  onQueryChange,
  onImagesGenerated,
  onImageSelect,
  uniqueId,
  shortcutModifier,
}) => {
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [error, setError] = useState(null);
  const [googlePage, setGooglePage] = useState(1);
  const [generatingImages, setGeneratingImages] = useState([]);
  const prevImagesLengthRef = useRef(imageData.images?.length);

  const handleGenerateImages = async (type) => {
    setError(null);
    if (type === "ai") {
      setIsLoadingAI(true);
      const placeholderId = uuidv4();
      setGeneratingImages((prev) => [
        ...prev,
        { imageId: placeholderId, status: "generating" },
      ]);
      try {
        const response = await fetch(`${CONFIG.API_URL}/generate/ai-images`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            prompt: imageData.prompt,
            dimensions: { width: 512, height: 512 },
            timeout: 300000,
          }),
        });
        if (!response.ok) {
          throw new Error("Failed to generate AI images");
        }
        const data = await response.json();
        let images = data.images;
        if (Array.isArray(images) && images.length > 0 && Array.isArray(images[0])) {
          images = images.flat();
        }
        if (images && images.length > 0) {
          onImagesGenerated(images);
        }
        setGeneratingImages((prev) =>
          prev.filter((img) => img.imageId !== placeholderId)
        );
      } catch (error) {
        console.error("Error generating AI images:", error);
        setError("Failed to generate AI images. Please try again.");
        setGeneratingImages((prev) =>
          prev.filter((img) => img.imageId !== placeholderId)
        );
      } finally {
        setIsLoadingAI(false);
      }
    } else if (type === "google") {
      setIsLoadingGoogle(true);
      try {
        const response = await fetch(`${CONFIG.API_URL}/generate/google-images`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            prompt: imageData.query || imageData.prompt,
            aspectRatio: "16/9",
            page: googlePage,
          }),
        });
        if (!response.ok) {
          throw new Error("Failed to generate Google images");
        }
        const data = await response.json();
        if (data.images && data.images.length > 0) {
          onImagesGenerated(data.images);
          setGooglePage((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error generating Google images:", error);
        setError("Failed to generate Google images. Please try again.");
      } finally {
        setIsLoadingGoogle(false);
      }
    }
  };

  // Keyboard shortcuts for image generation
  useKeyboardShortcut({
    modifier: shortcutModifier,
    keyTrigger: "g",
    handler: () => handleGenerateImages("google"),
  });
  useKeyboardShortcut({
    modifier: shortcutModifier,
    keyTrigger: "a",
    handler: () => handleGenerateImages("ai"),
  });

  return (
    <Card>
      <CardHeader>
        <h5>{label}</h5>
      </CardHeader>
      <CardBody>
        <label className="form-label">AI Prompt</label>
        <input
          type="text"
          className="form-control mb-1"
          value={imageData.prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder='Must start with "realistic photography" and end with "high definition quality and details"'
        />
        <label className="form-label">Google Query</label>
        <input
          type="text"
          className="form-control mb-1"
          value={imageData.query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Enter Google Query"
        />
        {error && <div className="alert alert-danger mb-2">{error}</div>}
        <div className="mb-1 d-grid gap-2 d-md-flex">
          <button
            className="btn btn-success flex-grow-1"
            onClick={() => handleGenerateImages("ai")}
            disabled={isLoadingAI}
          >
            {isLoadingAI ? "Loading..." : "Generate AI"}
          </button>
          <button
            className="btn btn-warning flex-grow-1"
            onClick={() => handleGenerateImages("google")}
            disabled={isLoadingGoogle}
          >
            {isLoadingGoogle ? "Loading..." : "Get Google Images"}
          </button>
        </div>
        <div className="mt-2">
          {imageData.images && imageData.images.length > 0 ? (
            <div className="image-grid">
              {imageData.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img.preview || img.image}
                  alt="result"
                  className={`image-item ${
                    imageData.selection === img ? "selected" : ""
                  }`}
                  onClick={() => onImageSelect(img)}
                  style={{ maxWidth: "100px", margin: "5px" }}
                />
              ))}
            </div>
          ) : (
            <p className="text-center">No Images Generated</p>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default ImageCard;
